import rgba from 'polished/lib/color/rgba';
import styled, { StyledComponent } from 'styled-components';

import * as Tabs from '#/components/common/tabs';
import { verticalSpacerCss } from '#/components/shared/Spacer';
import { gradientUnderline } from '#/components/shared/styles';

import { cFFFFFF, mainGridGapColor } from '#/colors';

interface Props {
  readonly activeTab?: string;
  readonly onTabChange?: (id: string) => void;
  readonly tabData: readonly Tabs.TabData[];
  readonly className?: string;
}

export default function TradeOverview(props: Props) {
  return <TradeOverviewContent {...props} />;
}

function TradeOverviewContent(props: Props) {
  const { activeTab, onTabChange, className, tabData } = props;

  return (
    <Container className={className}>
      <TabsRoot
        value={activeTab}
        onValueChange={onTabChange}
        defaultValue={tabData[0]?.id}
      >
        <TabsList>
          {tabData.map((tab) => (
            <TabsTrigger key={tab.id} disabled={tab.disabled} value={tab.id}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {tabData.map((tab) => (
          <TabsContent key={tab.id} value={tab.id}>
            {tab.content}
          </TabsContent>
        ))}
      </TabsRoot>
    </Container>
  );
}

const Container = styled.div`
  --TABS_LIST_HEIGHT: 50px;
  --TABS_LIST_GAP: 40px;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  overflow: auto;
`;

const TabsRoot = styled(Tabs.Root)`
  display: flex;
  flex: 1 1 0%;
  flex-direction: column;
`;

export const TabsTrigger: StyledComponent<typeof Tabs.Trigger, object> = styled(
  Tabs.Trigger,
)`
  background: none;
  border: none;
  color: ${rgba(cFFFFFF, 0.5)};
  padding: 0 7px;
  font-size: 13px;
  border-bottom: none;
  margin-bottom: -1px;
  position: relative;
  font-weight: 700;
  height: var(--TABS_LIST_HEIGHT);

  z-index: 1;

  &:hover {
    color: ${cFFFFFF};
  }

  &[data-state='active'] {
    color: ${cFFFFFF};
    ${gradientUnderline}
  }

  &:not(:first-of-type)::before {
    content: '';
    display: block;
    position: absolute;
    top: 0px;
    left: calc(var(--TABS_LIST_GAP) / 2 * -1);
    height: var(--TABS_LIST_HEIGHT);
    ${verticalSpacerCss}
  }
`;

export const TabsList: StyledComponent<typeof Tabs.List, object> = styled(
  Tabs.List,
)`
  display: flex;
  height: var(--TABS_LIST_HEIGHT);
  border-bottom: 1px solid ${mainGridGapColor};
  padding: 0 16px;
  gap: var(--TABS_LIST_GAP);
  align-items: center;
`;

export const TabsContent: StyledComponent<typeof Tabs.Content, object> = styled(
  Tabs.Content,
)`
  --MIN_HEIGHT: calc(var(--TABLE_ROW_HEIGHT) * var(--MIN_VISIBLE_ROWS));

  flex: 1 1 0%;
  overflow: auto;
  position: relative;
  min-height: var(--MIN_HEIGHT, initial);
`;
